import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientInformationForm } from '@/shared/forms/ClientInformationForm/ClientInformationForm.tsx';
import { ClientInformationFormValues } from '@/shared/forms/ClientInformationForm/schemas/getClientInformationFormSchema.ts';
import { usePositionContext } from '@/shared/context/PositionContext.tsx';
import { useSuspenseConstructionProject } from '@/clients/constructionProject/useConstructionProject.ts';
import { useOrganizationById } from '@/clients/organization/useOrganization.ts';
import { useUserSuspenseQuery } from '@/clients/users/useUsers.ts';

type ClientInformationPositionProps = {
    onContinue: () => void;
};

export const ClientInformationPosition = ({ onContinue }: ClientInformationPositionProps) => {
    const { t } = useTranslation();
    const { contextData, isEditing } = usePositionContext();
    const customerInfo = contextData.getCustomerInformation();

    if (!customerInfo) {
        throw new Error('customerInfo is required to render ClientInformationPosition');
    }

    const { data: constructionProject } = useSuspenseConstructionProject(customerInfo.constructionProjectId);
    const { data: organization } = useOrganizationById(customerInfo.organizationId);
    const { data: user } = useUserSuspenseQuery(customerInfo.userId);

    const handleSubmit = useCallback(
        (values: ClientInformationFormValues) => {
            contextData.setCustomerInformation({
                constructionProjectId: values.constructionProjectId,
                organizationId: values.organizationId,
                userId: values.userId,
                projectNumber: values.projectNumber,
                manualCostCenter: values.manualCostCenter,
            });
            onContinue();
        },
        [contextData, onContinue]
    );

    return (
        <ClientInformationForm
            defaultValues={{
                ...customerInfo,
                sourceCostCenter: constructionProject.costCenter,
            }}
            defaultValueLabels={{
                organization: organization.name,
                constructionProject: constructionProject.name,
                user: `${user.firstName} ${user.lastName}`,
                sourceCostCenter: t('sections.clientInformation.costCenter.sourceLabelPosition'),
                manualCostCenter: t('sections.clientInformation.costCenter.inputLabelPosition'),
            }}
            formRules={{
                organization: { disabled: true },
                constructionProject: { disabled: true },
                user: { disabled: true },
                costCenter: { required: false },
            }}
            onSubmit={handleSubmit}
            mode={isEditing ? 'edit' : 'create'}
        />
    );
};
